import { KeyboardEvent } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Flex } from '@rebass/grid';

import { BladeType, useBlade } from '../state/BladeProvider';

import BladeMenu from './BladeMenu';
import { QuoteBladeCard as Card } from './Card';
import * as TitleWithIcon from './TitleWithIcon';
import * as Text from './Text';
import * as Cta from './Cta';
import * as Link from './Link';
import CallToAction from './CallToAction';
import InlineLink from './InlineLink';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: -10px;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    /* IE10+ CSS styles go here */
    display: block;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    margin: -20px;
  }
`;

interface Props {
  className: string,
  // @ts-ignore giving error on Queries on gitlab
  cta: Queries.ContentfulButton & Queries.ContentfulLinkWrapper,
  title: string,
  closeMsg: string,
  // @ts-ignore giving error on Queries on gitlab
  menu: Queries.ContentfulIconButtonCard[],
  onKeyDown: () => void,
}

const QuoteBlade = ({
  className,
  cta,
  title,
  closeMsg,
  menu,
  onKeyDown,
}: Props) => {
  const { activeBlade, setActiveBlade } = useBlade();
  const isQuoteBladeOpen = () => activeBlade === BladeType.QUOTE;
  const updateBladeState = () => setActiveBlade(isQuoteBladeOpen() ? null : BladeType.QUOTE);
  const resetBladeState = () => setActiveBlade(null);
  const bladeState = activeBlade === BladeType.QUOTE;

  const closeBladeOnEsc = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      resetBladeState();
    }
  };

  return (
    <>
      {cta && cta.internal.type === 'ContentfulButton' ? (
        <CallToAction
          key={cta.id}
          className={className}
          variant={cta.buttonType as string}
          onClick={updateBladeState}
          aria-expanded={bladeState}
          onKeyDown={onKeyDown}
        >
          {cta.buttonLabel}
        </CallToAction>
      ) : (
        <InlineLink
          key={cta.id}
          className={className}
          onClick={updateBladeState}
          aria-expanded={bladeState}
          onKeyDown={onKeyDown}
        >
          {cta.linkText}
        </InlineLink>
      )}
      <BladeMenu
        title={title}
        closeMsg={closeMsg}
        isOpen={bladeState}
        action={updateBladeState}
        closeAllMenusOnEsc={closeBladeOnEsc}
        resetState={resetBladeState}
      >
        <Wrapper>
          {menu.map(
            ({
              id,
              heading,
              iconImageDam,
              paragraph,
              button,
              link,
            }) => (
              <Card
                key={id}
              >
                <TitleWithIcon.Card
                  src={iconImageDam?.asset?.localImage?.publicURL}
                  alt={iconImageDam?.altText}
                  fontWeight="bold"
                >
                  {heading}
                </TitleWithIcon.Card>
                <Text.Card m={['auto 0 2rem 0']}>
                  {paragraph?.paragraph}
                </Text.Card>
                <Flex alignItems={['center']} m={['auto 0 0 0']}>
                  <Cta.Primary
                    href={button?.buttonUrl as string}
                    m={[
                      '0 1rem 0 0 ',
                      '0 1rem 0 0 ',
                      '0 1.25rem 0 0 ',
                      '0 2rem 0 0 ',
                    ]}
                    minwidth={['130px', '130px', '150px', '150px']}
                  >
                    {button?.buttonLabel}
                  </Cta.Primary>
                  <Link.Secondary
                    href={link?.linkUrl}
                    onClick={() => updateBladeState()}
                  >
                    {link?.linkText}
                  </Link.Secondary>
                </Flex>
              </Card>
            ),
          )}
        </Wrapper>
      </BladeMenu>
    </>
  );
};

export default QuoteBlade;
